import clsx from "clsx";
import type React from "react";
import "./IconTile.css";

export interface IconTileProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "color"> {
  /** `sm` (32px) for dense rows, `md` (36px) for list rows and cards, `lg` (40px) for callouts and empty states. */
  size?: "sm" | "md" | "lg";
  /** Status colours tint the tile; `neutral` is for icons that only identify a kind of thing. */
  color?: "neutral" | "primary" | "success" | "warning" | "danger";
  /** A custom tint (any CSS colour), for things the user colours themselves. Overrides `color`. */
  customColor?: string;
  /** `outline` (the default) has a border; `soft` is a tinted fill without one. */
  fill?: "outline" | "soft";
  /** `circle` for people and invitations; `square` for everything else. */
  shape?: "square" | "circle";
  /** Dashed and unfilled: something that doesn't exist yet, such as a pending invitation. */
  pending?: boolean;
  children: React.ReactNode;
}

/** An icon in a bordered square: the leading visual for list rows, cards and callouts. */
const IconTile = (props: IconTileProps) => {
  const {
    size = "md",
    color = "neutral",
    customColor,
    fill = "outline",
    shape = "square",
    pending = false,
    className,
    style,
    children,
    ...otherProps
  } = props;
  return (
    <span
      aria-hidden={true}
      className={clsx("calmlens-icon-tile", className)}
      data-size={size}
      data-color={customColor ? "custom" : color}
      data-fill={fill}
      data-shape={shape}
      data-pending={pending || undefined}
      style={
        customColor
          ? ({ "--icon-tile-color": customColor, ...style } as React.CSSProperties)
          : style
      }
      {...otherProps}
    >
      {children}
    </span>
  );
};

export default IconTile;
